import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import {
  TERRITORY_TAG_TYPE,
  OPTIMIZATION_VERSION_TAG_TYPE,
} from './constants';
import {
  applyVersion,
  upsertVersionSummary,
} from 'src/redux/slices/optimizationSlice';
import type {
  OptimizationResultPayload,
  MetricDisplayValue,
  RepAssignment,
  RefinementWarning,
  OptimizationVersionSummary,
  OptimizationVersionDetail,
} from 'src/redux/slices/optimizationSlice';

// Request/Response types
interface DeserializeTerritoryRequest {
  file: File;
}

interface PreviewTerritoryRequest {
  data: Omit<Partial<MedScout.Territory>, 'owner'> & {
    name: string;
    is_canonical: boolean;
    smooth_level: number;
    user?: number;
  };
  smooth: number;
}

interface PaginatedTerritoriesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MedScout.Territory[];
}

/**
 * Page parameter for infinite query pagination
 */
interface TerritoriesPageParam {
  page: number;
}

/**
 * Parameters for getAllTerritories query
 * Supports backend filtering, sorting, and search
 */
export interface GetAllTerritoriesParams {
  /** Text search (searches name, first_name, last_name, email) */
  search?: string;
  /** Filter by owner user ID(s) */
  owner?: number[];
  /** Filter by primary/canonical territory status */
  is_canonical?: boolean;
  /** Filter by optimized territory status */
  optimized?: boolean;
  /** Sort field */
  sort?: 'name' | 'owner';
  /** Sort direction */
  order?: 'asc' | 'desc';
}

// Bulk upload types
interface BulkUploadTerritoryResult {
  file_name: string;
  status: 'created' | 'updated';
  territory: MedScout.Territory;
}

interface BulkUploadTerritoryError {
  file_name: string;
  error: string;
}

export interface BulkUploadTerritoriesResponse {
  total: number;
  successes: number;
  failures: number;
  results: BulkUploadTerritoryResult[];
  errors: BulkUploadTerritoryError[];
}

interface BulkUploadTerritoriesRequest {
  files: File[];
  smooth_level?: number;
}

// Bulk territory operation types (for copy, reassign, delete, update-canonical)
export interface BulkTerritoryFailure {
  territory_id: number;
  error: string;
}

export interface BulkTerritoryResponse {
  successful_count: number;
  failed_count: number;
  failures: BulkTerritoryFailure[];
}

export interface BulkTerritoryRequest {
  territory_ids: number[];
}

export interface BulkTerritoryWithTargetUserRequest
  extends BulkTerritoryRequest {
  target_user_id: number;
}

// Create territories from optimization result types
interface CreateTerritoriesFromResultRequest {
  jobId: number;
  territories: Array<{
    index: number;
    name: string;
    user_id: number;
  }>;
  /**
   * Refinement version to save from. Omit for the active version; `0` targets
   * the original (v0) job result. Names/assignees come from the version-scoped
   * rep map server-side.
   */
  version_number?: number;
}

interface CreatedTerritory {
  id: number;
  name: string;
  is_canonical: boolean;
  optimized: boolean;
  owner: {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
  };
  index: number;
}

interface CreateTerritoriesFromResultResponse {
  results: CreatedTerritory[];
}

// Optimization job types
interface AdhocTerritoryRequest {
  geometry?: string[];
  exclude_geometry?: string[];
  points?: Array<Array<{ lat: number; lng: number }>>;
  circles?: Array<{ center: { lat: number; lng: number }; radius: number }>;
}

interface CreateOptimizationJobRequest {
  strategy_id: number;
  num_territories: number;
  granularity: 'state' | 'county' | 'zip_code';
  metric_codes: string[];
  metric_weights?: Record<string, number>;
  territory_ids?: number[];
  region_ids?: number[];
  adhoc_territory?: AdhocTerritoryRequest;
}

interface OptimizationJobSourceItem {
  id: string;
  label: string;
}

/**
 * Structured failure codes emitted by the optimization backend. Each code maps
 * to user-friendly copy + chat-native remediation CTAs in
 * `OptimizationSystemMessage`. A failed job may carry no code (null) when the
 * failure isn't one of these classified cases.
 */
export type OptimizationErrorCode =
  | 'REPS_NO_DATA'
  | 'INSUFFICIENT_DATA_FOR_TERRITORY_COUNT'
  | 'ALL_METRICS_EMPTY'
  | 'STRATEGY_FAILED';

export interface OptimizationJobApiResponse {
  id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  thread_id: number | null;
  strategy: { id: number; label: string } | null;
  /**
   * Entity focus for the job's data source ("provider"/"hcp" vs "site"), when
   * the backend can determine it. Present for code-set jobs (which have no
   * strategy); may be absent/null for older or indeterminate jobs. Preferred
   * over the strategy-details lookup when set. See getIsProviderFocused.
   */
  provider_type?: string | null;
  /** Code sets the territories were built from (jobs may use these vs a strategy). */
  code_set_ids?: number[] | null;
  regions: Array<{ id: number; name: string; type: string }> | null;
  territories: Array<{ id: number; name: string }> | null;
  num_territories: number;
  granularity: 'state' | 'county' | 'zip_code';
  metric_codes: string[];
  metric_weights: Record<string, number> | null;
  result: OptimizationResultPayload | null;
  // Strategy entries carry `strategy_metric_id`; custom (customer-dataset)
  // entries carry `customer_dataset_metric_id`. The id key present is the
  // discriminator — never both.
  metric_display_values: {
    strategy_metric_id?: number;
    customer_dataset_metric_id?: number;
    label: string;
    display_options: {
      code: string;
      type: string;
      label: string;
    }[];
  }[];
  // Seed-derived rep assignment per territory (keyed by stringified territory
  // index). Populated only for runs seeded from a committed rep_seed dataset.
  rep_assignments?: Record<
    string,
    { label: string; user_id: number | null }
  > | null;
  source: {
    states: OptimizationJobSourceItem[];
    counties: OptimizationJobSourceItem[];
    zip_codes: OptimizationJobSourceItem[];
    excluded_states: OptimizationJobSourceItem[];
    excluded_counties: OptimizationJobSourceItem[];
    excluded_zip_codes: OptimizationJobSourceItem[];
    drawn: boolean;
    circles: unknown[];
  } | null;
  drawn_polygon: Array<{
    boundary: Array<{ lat: number; lng: number }>;
    holes: unknown[];
  }> | null;
  error_message: string | null;
  error_code: OptimizationErrorCode | null;
  created_at: string;
  updated_at: string;
}

// ─── Refinement (interactive territory refinement) ────────────────────────
// Only `move_units` is exposed over REST — it backs the map's reassign gesture.
// Split/merge/rebalance are chat-only (the agent calls the service in-process).

/**
 * Request body for the refinement commit + preview endpoints. The backend
 * accepts only `operation_type: 'move_units'`; the FE sets it internally so
 * callers just pass the units + target.
 */
export interface MoveUnitsRefinementRequest {
  jobId: number;
  unit_ids: string[];
  target_index: number;
}

/**
 * Response from the preview endpoint — score deltas + warnings for a pending
 * move, computed in memory without persisting a version.
 */
export interface RefinementPreviewResponse {
  old_balance_score: number;
  new_balance_score: number;
  balance_score_delta: number;
  warnings: RefinementWarning[];
  index_remapping: Record<string, number>;
  result: OptimizationResultPayload;
  metric_display_values: MetricDisplayValue[];
}

/** Shape of the versions-list endpoint envelope. */
interface JobVersionsListResponse {
  results: OptimizationVersionSummary[];
}

// Boundary types for territory builder
interface BoundaryGeometry {
  id: string | number;
  label: string;
  geometry: number[][][];
}

/**
 * V2 Paginated Boundaries API types
 * Used with the new paginated boundaries endpoint that supports infinite loading
 */
export type BoundaryType = 'state' | 'county' | 'zip_code';

export interface PaginatedBoundariesV2Response {
  count: number;
  next: string | null;
  previous: string | null;
  results: BoundaryGeometry[];
}

export interface GetBoundariesV2Params {
  type: BoundaryType;
  boundingBox: [[number, number], [number, number]] | null;
}

export interface GetBoundariesByIdsParams {
  ids: string[];
}

interface BoundariesByIdsResponse {
  count: number;
  results: BoundaryGeometry[];
}

// Backend rejects boundaries/by-ids requests with more ids than this
const BOUNDARIES_BY_IDS_MAX_IDS = 5000;

/**
 * Builds the URL for the v2 boundaries API
 */
const buildBoundariesV2Url = (
  type: BoundaryType,
  page: number,
  pageSize: number,
  boundingBox: [[number, number], [number, number]] | null
): string => {
  const params = new URLSearchParams();
  params.set('type', type);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  params.set('with_geometry', 'true');
  if (boundingBox) {
    params.set('bounding_box', JSON.stringify(boundingBox));
  }
  return `v1/accounts/boundaries/?${params.toString()}`;
};

/**
 * Territory API
 * Handles territory-related operations including file uploads, previews, and management
 */
export const territoryApi = createApi({
  reducerPath: 'territory',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [TERRITORY_TAG_TYPE, OPTIMIZATION_VERSION_TAG_TYPE],
  endpoints: (builder) => ({
    /**
     * Get all territories (paginated)
     * Recursively fetches all pages and returns combined results
     * Supports backend filtering by owner, is_canonical, and sorting
     */
    getAllTerritories: builder.query<
      MedScout.Territory[],
      GetAllTerritoriesParams | void
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const allTerritories: MedScout.Territory[] = [];
        const params: GetAllTerritoriesParams =
          arg && typeof arg === 'object' ? arg : {};

        // Build query params
        const searchParams = new URLSearchParams();
        searchParams.set('page', '1');
        searchParams.set('pageSize', '1000');

        if (params.search) {
          searchParams.set('q', params.search);
        }

        // Handle owner array (repeated params: ?owner=1&owner=2)
        if (params.owner?.length) {
          params.owner.forEach((id) =>
            searchParams.append('owner', String(id))
          );
        }

        if (params.is_canonical !== undefined) {
          searchParams.set('is_canonical', String(params.is_canonical));
        }

        if (params.optimized !== undefined) {
          searchParams.set('optimized', String(params.optimized));
        }

        // Sort params must be provided together
        if (params.sort && params.order) {
          searchParams.set('sort', params.sort);
          searchParams.set('order', params.order);
        }

        let url = `v1/accounts/territories/?${searchParams.toString()}`;

        while (url) {
          const result = await fetchWithBQ(url);
          if (result.error) return { error: result.error };

          const data = result.data as PaginatedTerritoriesResponse;
          allTerritories.push(...data.results);

          // Get next page URL if it exists
          if (data.next) {
            try {
              const nextUrl = new URL(data.next);
              // Extract path after /api/ prefix and include query params
              url = nextUrl.pathname.replace(/^\/?api\//, '') + nextUrl.search;
            } catch {
              // If URL parsing fails, fall back to the original value
              url = data.next;
            }
          } else {
            url = '';
          }
        }

        return { data: allTerritories };
      },
      providesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Get a single territory by ID
     */
    getTerritory: builder.query<MedScout.Territory, { territoryId: string }>({
      query: ({ territoryId }) => `v1/accounts/territories/${territoryId}/`,
      providesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Get territories with infinite scroll pagination
     * Returns 250 territories per page, loads more as user scrolls
     */
    getTerritoriesPaginated: builder.infiniteQuery<
      PaginatedTerritoriesResponse,
      GetAllTerritoriesParams | void,
      TerritoriesPageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 1 },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage.next) return undefined;
          return { page: lastPageParam.page + 1 };
        },
      },
      query({ queryArg, pageParam }) {
        const params = queryArg && typeof queryArg === 'object' ? queryArg : {};
        const searchParams = new URLSearchParams();

        searchParams.set('page', String(pageParam.page));
        searchParams.set('pageSize', '250');

        if (params.search) {
          searchParams.set('q', params.search);
        }

        // Handle owner array (repeated params: ?owner=1&owner=2)
        if (params.owner?.length) {
          params.owner.forEach((id) =>
            searchParams.append('owner', String(id))
          );
        }

        if (params.is_canonical !== undefined) {
          searchParams.set('is_canonical', String(params.is_canonical));
        }

        if (params.optimized !== undefined) {
          searchParams.set('optimized', String(params.optimized));
        }

        // Sort params must be provided together
        if (params.sort && params.order) {
          searchParams.set('sort', params.sort);
          searchParams.set('order', params.order);
        }

        return `v1/accounts/territories/?${searchParams.toString()}`;
      },
      providesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Get user's assigned (canonical) territories with geometry
     * Returns territories where is_canonical=true that the user has access to
     * Recursively fetches all pages and returns combined results
     */
    getAssignedTerritories: builder.query<MedScout.Territory[], void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const allTerritories: MedScout.Territory[] = [];
        let url = `v1/accounts/territories/assigned/?include_geometry=true&page=1&pageSize=1000`;

        while (url) {
          const result = await fetchWithBQ(url);
          if (result.error) return { error: result.error };

          const data = result.data as PaginatedTerritoriesResponse;
          allTerritories.push(...data.results);

          // Get next page URL if it exists
          if (data.next) {
            try {
              const nextUrl = new URL(data.next);
              // Extract path after /api/ prefix and include query params
              url = nextUrl.pathname.replace(/^\/?api\//, '') + nextUrl.search;
            } catch {
              // If URL parsing fails, fall back to the original value
              url = data.next;
            }
          } else {
            url = '';
          }
        }

        return { data: allTerritories };
      },
      providesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Deserialize a territory CSV file
     * Accepts a CSV file and returns the parsed territory data
     */
    deserializeTerritory: builder.mutation<
      MedScout.Territory,
      DeserializeTerritoryRequest
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('territory', file);
        return {
          url: '/v1/accounts/territories/import/',
          method: 'POST',
          body: formData,
          // Note: FormData handled automatically by baseQuery
        };
      },
    }),
    previewTerritory: builder.mutation<
      MedScout.Territory,
      PreviewTerritoryRequest
    >({
      query: ({ data, smooth }) => ({
        url: `v1/accounts/territories/?preview=1&smooth=${smooth}`,
        method: 'POST',
        body: data,
      }),
    }),
    uploadTerritory: builder.mutation<MedScout.Territory, MedScout.Territory>({
      query: (data) => ({
        url: `v1/accounts/territories/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Bulk upload multiple territory files
     * Accepts CSV, KML, or KMZ files (max 50 files)
     * Returns success/failure status for each file
     */
    bulkUploadTerritories: builder.mutation<
      BulkUploadTerritoriesResponse,
      BulkUploadTerritoriesRequest
    >({
      query: ({ files, smooth_level }) => {
        const formData = new FormData();
        // Field name must be 'files' (plural) - Django uses getlist()
        files.forEach((file) => formData.append('files', file));
        if (smooth_level !== undefined) {
          formData.append('smooth_level', String(smooth_level));
        }
        return {
          url: 'v1/accounts/territories/bulk-upload/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Export territory zip codes as CSV
     * Downloads a CSV file containing all ZIP codes that intersect with the territory polygon
     * @returns Blob - CSV file for download
     */
    exportTerritoryZipCodes: builder.mutation<Blob, { territoryId: number }>({
      query: ({ territoryId }) => ({
        url: `v1/accounts/territories/${territoryId}/zip-codes/export/`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /**
     * Bulk copy territories to another user
     * Creates copies of the specified territories assigned to the target user
     * Copied territories will have is_canonical=false
     * @note Support user only endpoint
     */
    bulkCopyTerritories: builder.mutation<
      BulkTerritoryResponse,
      BulkTerritoryWithTargetUserRequest
    >({
      query: ({ territory_ids, target_user_id }) => ({
        url: 'v1/accounts/territories/bulk-copy/',
        method: 'POST',
        body: { territory_ids, target_user_id },
      }),
      invalidatesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Bulk reassign territories to another user
     * Transfers ownership of the specified territories to the target user
     * Reassigned territories will have is_canonical=false
     * @note Support user only endpoint
     */
    bulkReassignTerritories: builder.mutation<
      BulkTerritoryResponse,
      BulkTerritoryWithTargetUserRequest
    >({
      query: ({ territory_ids, target_user_id }) => ({
        url: 'v1/accounts/territories/bulk-reassign/',
        method: 'POST',
        body: { territory_ids, target_user_id },
      }),
      invalidatesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Bulk delete territories
     * Soft-deletes the specified territories and removes their strategy connectors
     * @note Support user only endpoint
     */
    bulkDeleteTerritories: builder.mutation<
      BulkTerritoryResponse,
      BulkTerritoryRequest
    >({
      query: ({ territory_ids }) => ({
        url: 'v1/accounts/territories/bulk-delete/',
        method: 'POST',
        body: { territory_ids },
      }),
      invalidatesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Bulk update canonical status of territories
     * Sets the specified territories as canonical for their respective owners
     * Only one territory per user can be selected in a single request
     * @note Support user only endpoint
     */
    bulkUpdateCanonicalTerritories: builder.mutation<
      BulkTerritoryResponse,
      BulkTerritoryRequest
    >({
      query: ({ territory_ids }) => ({
        url: 'v1/accounts/territories/bulk-update-canonical/',
        method: 'POST',
        body: { territory_ids },
      }),
      invalidatesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Create an optimization job (async)
     * Submits optimization parameters and returns a job with pending/running status.
     * Poll with getOptimizationJob to wait for completion.
     */
    createOptimizationJob: builder.mutation<
      OptimizationJobApiResponse,
      CreateOptimizationJobRequest
    >({
      query: (data) => ({
        url: 'v1/territory-optimization/jobs/',
        method: 'POST',
        body: data,
      }),
    }),

    /**
     * Get an optimization job by ID
     * Used to poll for job completion after async creation.
     */
    getOptimizationJob: builder.query<OptimizationJobApiResponse, number>({
      query: (jobId) => `v1/territory-optimization/jobs/${jobId}/`,
    }),

    /**
     * List optimization jobs with optional source filter and pagination.
     * Use source='wizard' for the landing page (excludes chat-based jobs).
     */
    getOptimizationJobs: builder.query<
      { count: number; next: string | null; previous: string | null; results: OptimizationJobApiResponse[] },
      { source?: 'wizard' | 'thread'; page?: number; pageSize?: number }
    >({
      query: ({ source, page, pageSize }) => {
        const params = new URLSearchParams();
        if (source) params.set('source', source);
        if (page) params.set('page', String(page));
        if (pageSize) params.set('pageSize', String(pageSize));
        const qs = params.toString();
        return `v1/territory-optimization/jobs/${qs ? `?${qs}` : ''}`;
      },
    }),

    /**
     * Update an optimization job (partial update) and re-run it.
     * Resets the job status to PENDING and clears previous results.
     * Returns 409 Conflict if the job is currently PENDING or RUNNING.
     */
    updateOptimizationJob: builder.mutation<
      OptimizationJobApiResponse,
      { jobId: number } & Partial<CreateOptimizationJobRequest>
    >({
      query: ({ jobId, ...data }) => ({
        url: `v1/territory-optimization/jobs/${jobId}/`,
        method: 'PATCH',
        body: data,
      }),
    }),

    /**
     * Delete an optimization job.
     * Can delete jobs in any status (PENDING, RUNNING, COMPLETED, FAILED).
     * Returns 204 No Content on success.
     */
    deleteOptimizationJob: builder.mutation<void, number>({
      query: (jobId) => ({
        url: `v1/territory-optimization/jobs/${jobId}/`,
        method: 'DELETE',
      }),
    }),

    /**
     * Create territories from an optimization job result
     * Takes selected territories with names and assigned reps
     */
    createTerritoriesFromResult: builder.mutation<
      CreateTerritoriesFromResultResponse,
      CreateTerritoriesFromResultRequest
    >({
      query: ({ jobId, territories, version_number }) => ({
        url: `v1/territory-optimization/jobs/${jobId}/create-territories/`,
        method: 'POST',
        // Omit version_number entirely when unset so the backend defaults to the
        // active version (0 is a valid value — the v0 baseline — so don't coerce).
        body:
          version_number === undefined
            ? { territories }
            : { territories, version_number },
      }),
      invalidatesTags: [TERRITORY_TAG_TYPE],
    }),

    /**
     * Commit a map reassign (move_units) as a new refinement version.
     * Returns the full version detail; on success the active version is applied
     * to the optimization slice so the results map reflects the edit, and the
     * new summary is added to the version strip.
     */
    createRefinement: builder.mutation<
      OptimizationVersionDetail,
      MoveUnitsRefinementRequest
    >({
      query: ({ jobId, unit_ids, target_index }) => ({
        url: `v1/territory-optimization/jobs/${jobId}/refinements/`,
        method: 'POST',
        body: { operation_type: 'move_units', unit_ids, target_index },
      }),
      invalidatesTags: [OPTIMIZATION_VERSION_TAG_TYPE],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(applyVersion(data));
          // Strip the snapshot-only fields — the version list holds summaries.
          const {
            result: _result,
            rep_assignments: _repAssignments,
            metric_display_values: _metricDisplayValues,
            ...summary
          } = data;
          dispatch(upsertVersionSummary(summary));
        } catch {
          // Mutation error is surfaced to the caller (optimistic revert lives
          // in the Reassign-mode commit flow); nothing to apply here.
        }
      },
    }),

    /**
     * Preview a move_units refinement — score deltas + warnings, no persist.
     * Backs the live balance preview while units are pending in Reassign mode.
     */
    previewRefinement: builder.mutation<
      RefinementPreviewResponse,
      MoveUnitsRefinementRequest
    >({
      query: ({ jobId, unit_ids, target_index }) => ({
        url: `v1/territory-optimization/jobs/${jobId}/refinements/preview/`,
        method: 'POST',
        body: { operation_type: 'move_units', unit_ids, target_index },
      }),
    }),

    /**
     * List a job's refinement version history (v0 baseline first, then edits
     * ascending). Feeds the version strip.
     */
    getJobVersions: builder.query<OptimizationVersionSummary[], number>({
      query: (jobId) => `v1/territory-optimization/jobs/${jobId}/versions/`,
      transformResponse: (response: JobVersionsListResponse) =>
        response.results,
      providesTags: [OPTIMIZATION_VERSION_TAG_TYPE],
    }),

    /**
     * Get the full snapshot for one version (version 0 → the original result).
     * Used to render an older version in the compare view, and to hydrate the
     * active version on load.
     */
    getJobVersion: builder.query<
      OptimizationVersionDetail,
      { jobId: number; versionNumber: number }
    >({
      query: ({ jobId, versionNumber }) =>
        `v1/territory-optimization/jobs/${jobId}/versions/${versionNumber}/`,
      providesTags: [OPTIMIZATION_VERSION_TAG_TYPE],
    }),

    /**
     * Delete (undo) a refinement version. The backend falls the active result
     * back to the highest remaining version and pushes a `result_version` WS
     * event so the map re-renders.
     */
    deleteVersion: builder.mutation<
      void,
      { jobId: number; versionNumber: number }
    >({
      query: ({ jobId, versionNumber }) => ({
        url: `v1/territory-optimization/jobs/${jobId}/versions/${versionNumber}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [OPTIMIZATION_VERSION_TAG_TYPE],
    }),

    /**
     * Get boundaries V2 with parallel batch fetching
     * Fetches all pages in parallel batches of 10 for improved performance
     * @param type - Boundary type: 'state', 'county', or 'zip_code'
     * @param boundingBox - Geographic bounding box [[maxLat, minLng], [minLat, maxLng]]
     */
    getBoundariesV2: builder.query<BoundaryGeometry[], GetBoundariesV2Params>({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const { type, boundingBox } = args;
        const BATCH_SIZE = 10;
        const PAGE_SIZE = 1000;

        try {
          // 1. Fetch first page to get total count
          const firstPageResult = await baseQuery({
            url: buildBoundariesV2Url(type, 1, PAGE_SIZE, boundingBox),
          });

          if (firstPageResult.error) {
            return { error: firstPageResult.error };
          }

          const firstPage =
            firstPageResult.data as PaginatedBoundariesV2Response;
          const totalCount = firstPage.count;
          const totalPages = Math.ceil(totalCount / PAGE_SIZE);
          const allBoundaries: BoundaryGeometry[] = [...firstPage.results];

          // 2. Fetch remaining pages in parallel batches
          for (
            let startPage = 2;
            startPage <= totalPages;
            startPage += BATCH_SIZE
          ) {
            const endPage = Math.min(startPage + BATCH_SIZE - 1, totalPages);
            const pageNumbers = Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i
            );

            const batchResults = await Promise.all(
              pageNumbers.map((page) =>
                baseQuery({
                  url: buildBoundariesV2Url(type, page, PAGE_SIZE, boundingBox),
                })
              )
            );

            // Collect results from batch - fail fast if any page fails
            for (const result of batchResults) {
              if (result.error) {
                return { error: result.error };
              }
              const pageData = result.data as PaginatedBoundariesV2Response;
              allBoundaries.push(...pageData.results);
            }
          }

          return { data: allBoundaries };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Failed to fetch boundaries',
            },
          };
        }
      },
    }),

    /**
     * Fetch boundary geometries by their Elasticsearch document IDs.
     * The backend caps each request at 5000 ids, so larger id sets are split
     * into parallel POSTs and merged. Unknown ids are silently omitted and
     * result order is not guaranteed.
     */
    getBoundariesByIds: builder.query<
      BoundaryGeometry[],
      GetBoundariesByIdsParams
    >({
      async queryFn({ ids }, _api, _extraOptions, baseQuery) {
        const idBatches: string[][] = [];
        for (let i = 0; i < ids.length; i += BOUNDARIES_BY_IDS_MAX_IDS) {
          idBatches.push(ids.slice(i, i + BOUNDARIES_BY_IDS_MAX_IDS));
        }

        const batchResults = await Promise.all(
          idBatches.map((batch) =>
            baseQuery({
              url: 'v1/accounts/boundaries/by-ids/',
              method: 'POST',
              body: { boundary_ids: batch },
            })
          )
        );

        // Fail fast if any batch fails so callers never see a partial set
        const allBoundaries: BoundaryGeometry[] = [];
        for (const result of batchResults) {
          if (result.error) {
            return { error: result.error };
          }
          allBoundaries.push(
            ...(result.data as BoundariesByIdsResponse).results
          );
        }

        return { data: allBoundaries };
      },
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetAllTerritoriesQuery,
  useGetTerritoryQuery,
  useLazyGetTerritoryQuery,
  useGetTerritoriesPaginatedInfiniteQuery,
  useGetAssignedTerritoriesQuery,
  useDeserializeTerritoryMutation,
  usePreviewTerritoryMutation,
  useUploadTerritoryMutation,
  useBulkUploadTerritoriesMutation,
  useExportTerritoryZipCodesMutation,
  useBulkCopyTerritoriesMutation,
  useBulkReassignTerritoriesMutation,
  useBulkDeleteTerritoriesMutation,
  useBulkUpdateCanonicalTerritoriesMutation,
  useCreateOptimizationJobMutation,
  useGetOptimizationJobQuery,
  useGetOptimizationJobsQuery,
  useUpdateOptimizationJobMutation,
  useDeleteOptimizationJobMutation,
  useCreateTerritoriesFromResultMutation,
  useCreateRefinementMutation,
  usePreviewRefinementMutation,
  useGetJobVersionsQuery,
  useLazyGetJobVersionsQuery,
  useGetJobVersionQuery,
  useLazyGetJobVersionQuery,
  useDeleteVersionMutation,
  useGetBoundariesV2Query,
  useGetBoundariesByIdsQuery,
} = territoryApi;
